/**
 * Phase 6E.2 — Winhouse Dashboard foundation (unified, program-agnostic).
 *
 * Modular structure so future programs (Selective, IELTS, GMAT, VCE, SAT, GRE,
 * Coding, CBC) plug in without separate dashboards. Each module is a small,
 * link-out card that points to the existing portal pages (no rebuild).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  GraduationCap, BookOpen, FileText, Trophy, Sparkles, MessageCircle,
  Award, Star, CreditCard, Mail, CalendarDays, ListTodo, Bell, Layers,
  ArrowRight, ChevronDown, Loader2, Flame,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import DailyChallengeWidget from '@/components/shared/DailyChallengeWidget';
import GamificationPanel from '@/components/portal/GamificationPanel';
import JourneyMapCard from '@/components/portal/JourneyMapCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const MODULES = [
  { id: 'my-learning', icon: BookOpen, title: 'My Learning', sub: 'Continue topics & lessons', to: '/student/learning', programs: ['selective-entry', 'ielts', 'gmat', 'vce', 'sat', 'gre', 'coding', 'general'] },
  { id: 'my-classes', icon: GraduationCap, title: 'My Classes', sub: 'Live + recordings', to: '/student/classes', programs: ['*'] },
  { id: 'learning-centre', icon: Star, title: 'Learning Centre', sub: 'Topics · practice · AI Coach', to: '/student/learning', programs: ['selective-entry', 'ielts', 'gmat', 'vce'] },
  { id: 'resources', icon: FileText, title: 'Resources', sub: 'Formulas · grammar · vocab', to: '/resources/selective-entry', programs: ['*'] },
  { id: 'mock-exams', icon: Trophy, title: 'Mock Exams', sub: 'Full-length papers', to: '/student/exams', programs: ['selective-entry', 'gmat', 'sat', 'gre'] },
  { id: 'discussion', icon: MessageCircle, title: 'Discussion Hub', sub: 'Chat with your class', to: '/student/learning?tab=hub', programs: ['*'] },
  { id: 'achievements', icon: Award, title: 'Achievements', sub: 'Badges · certificates', to: '/student/exams?tab=achievements', programs: ['*'] },
  { id: 'leaderboard', icon: Sparkles, title: 'Leaderboard', sub: 'Weekly & monthly ranks', to: '/student/exams?tab=leaderboard', programs: ['*'] },
  { id: 'payments', icon: CreditCard, title: 'Payments', sub: 'Plans · invoices · receipts', to: '/student/dashboard#payments', programs: ['*'] },
  { id: 'messages', icon: Mail, title: 'Messages', sub: 'Tutor + admin chat', to: '/student/dashboard#messages', programs: ['*'] },
  { id: 'timetable', icon: CalendarDays, title: 'Timetable', sub: 'Weekly schedule & PDF', to: '/student/dashboard#timetable', programs: ['*'] },
  { id: 'tasks', icon: ListTodo, title: 'Upcoming Tasks', sub: 'Homework · deadlines', to: '/student/dashboard#tasks', programs: ['*'] },
  { id: 'notifications', icon: Bell, title: 'Notifications', sub: 'Daily nudges & reminders', to: '/student/dashboard#alerts', programs: ['*'] },
  { id: 'programs', icon: Layers, title: 'Programs', sub: 'Switch program / enrol', to: '/dashboard#programs', programs: ['*'] },
];

const moduleVisibleForProgram = (m, programSlug) => {
  if (!programSlug) return m.programs.includes('*');
  return m.programs.includes('*') || m.programs.includes(programSlug);
};

const greetingFor = (hour) => {
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const ProgramSwitcher = ({ programs, active, onChange }) => {
  const [open, setOpen] = useState(false);
  if (!programs || programs.length === 0) {
    return (
      <Link to="/get-started" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-bold text-xs uppercase tracking-wider"
        data-testid="dash-enrol">
        Enrol in a Program <ArrowRight className="h-3 w-3" />
      </Link>
    );
  }
  const activeP = programs.find(p => p.slug === active) || programs[0];
  return (
    <div className="relative" data-testid="program-switcher">
      <button onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border border-gray-200 hover:border-gray-400 text-sm font-bold text-gray-900"
        data-testid="program-switcher-trigger">
        <Layers className="h-4 w-4 text-red-700" /> {activeP.name}
        <ChevronDown className={`h-3.5 w-3.5 text-gray-500 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-2xl shadow-lg z-30 p-1" data-testid="program-switcher-menu">
          {programs.map(p => (
            <button key={p.slug} onClick={() => { onChange(p.slug); setOpen(false); }}
              data-testid={`program-option-${p.slug}`}
              className={`w-full text-left px-3 py-2.5 rounded-xl flex items-center justify-between gap-2 text-sm hover:bg-gray-50 ${p.slug === active ? 'bg-gray-50' : ''}`}>
              <div className="min-w-0">
                <div className="font-bold text-gray-900 truncate">{p.name}</div>
                <div className="text-[10px] text-gray-500 truncate">{p.subject_count} subjects · {p.payment_status?.toUpperCase()}</div>
              </div>
              {p.slug === active && <span className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Active</span>}
            </button>
          ))}
          <Link to="/get-started" className="block px-3 py-2.5 rounded-xl text-sm text-red-700 font-bold hover:bg-red-50 mt-1 border-t border-gray-100" onClick={() => setOpen(false)}>
            + Enrol in another program
          </Link>
        </div>
      )}
    </div>
  );
};

const WinhouseDashboardPage = () => {
  const [snapshot, setSnapshot] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobile, setMobile] = useState(false);

  const fetchAll = async () => {
    try {
      const [s, p] = await Promise.all([
        ax.get('/api/me/student/dashboard-snapshot'),
        ax.get('/api/me/student/programs'),
      ]);
      setSnapshot(s.data);
      setPrograms(p.data?.items || []);
      setActive(p.data?.active_program_slug || null);
    } catch (_e) { /* will redirect via login link below */ }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const switchProgram = async (slug) => {
    try {
      await ax.post('/api/me/student/active-program', { program_slug: slug });
      setActive(slug);
    } catch (_e) { /* silent — UI updates locally */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-red-700" />
      </div>
    );
  }
  if (!snapshot) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" data-testid="dash-needs-login">
        <h1 className="text-2xl font-bold mb-3">Winhouse Dashboard</h1>
        <p className="text-gray-600 mb-5">Please sign in to your student account to view your dashboard.</p>
        <Link to="/student/login" className="px-5 h-11 inline-flex items-center bg-black text-[#D3D86F] rounded-full font-bold uppercase tracking-wider text-xs">Student Login</Link>
      </div>
    );
  }

  const user = snapshot.user || {};
  const firstName = user.first_name || (user.email || '').split('@')[0];
  const hour = snapshot.greeting_hour_utc ?? 9;
  const greeting = greetingFor(hour);
  const visible = MODULES.filter(m => moduleVisibleForProgram(m, active));
  const next = snapshot.upcoming_lessons?.[0];

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex flex-col" data-testid="winhouse-dashboard">
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <main className="flex-1 max-w-6xl mx-auto px-6 pt-24 pb-12 w-full">

        {/* Hero */}
        <header className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[3px] font-bold text-red-700">Winhouse Dashboard</div>
            <h1 className="text-2xl sm:text-3xl font-bold mt-0.5" data-testid="dash-greeting">{greeting}, {firstName} 👋</h1>
            <p className="text-sm text-gray-500 mt-1">Your unified study workspace — all programs, one home.</p>
          </div>
          <ProgramSwitcher programs={programs} active={active} onChange={switchProgram} />
        </header>

        {/* Stat strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
          <Stat label="Programs" value={snapshot.programs_count} tone="sky" testid="dash-stat-programs" icon={Layers} />
          <Stat label="XP" value={snapshot.xp} tone="amber" testid="dash-stat-xp" icon={Sparkles} />
          <Stat label="Streak" value={`${snapshot.streak_days}d`} tone="red" testid="dash-stat-streak" icon={Flame} />
          <Stat label="Sessions done" value={snapshot.sessions_completed} tone="emerald" testid="dash-stat-sessions" icon={Trophy} />
        </div>

        {/* Continue learning / Next lesson */}
        {next && (
          <Link to="/student/dashboard" className="block bg-black text-white rounded-2xl p-4 mb-5 hover:bg-gray-900 transition" data-testid="dash-next-lesson">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#D3D86F] font-bold">Continue learning</div>
                <div className="font-bold mt-0.5 truncate">{next.subject || 'Class'} · {next.tutor_name || 'Tutor'}</div>
                <div className="text-[11px] text-white/70 truncate">{new Date(next.start_time).toLocaleString()}</div>
              </div>
              <span className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-[#D3D86F] text-black text-[11px] font-bold uppercase tracking-wider">Open <ArrowRight className="h-3 w-3" /></span>
            </div>
          </Link>
        )}

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Modules grid */}
          <section className="lg:col-span-2 space-y-5" data-testid="dash-main-col">
            <JourneyMapCard />
            <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="dash-modules">
              <h2 className="text-sm font-bold mb-3 flex items-center gap-1.5"><Layers className="h-4 w-4 text-red-700" /> Modules</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {visible.map(m => (
                  <Link key={m.id} to={m.to} data-testid={`dash-module-${m.id}`}
                    className="group block bg-[#FAF9F6] rounded-xl border border-gray-100 hover:border-red-600 hover:-translate-y-0.5 transition p-4">
                    <div className="w-10 h-10 rounded-lg bg-red-50 text-red-700 flex items-center justify-center mb-2">
                      <m.icon className="h-5 w-5" />
                    </div>
                    <div className="font-bold text-sm">{m.title}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{m.sub}</div>
                    <span className="text-[10px] uppercase tracking-wider text-red-700 font-bold inline-flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition">Open <ArrowRight className="h-3 w-3" /></span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Side column: Gamification + Daily Challenge */}
          <aside className="space-y-4" data-testid="dash-side">
            <GamificationPanel />
            <DailyChallengeWidget />
          </aside>
        </div>

        {/* Empty-state suggestion if no programs */}
        {programs.length === 0 && (
          <section className="mt-6 bg-white rounded-2xl border-2 border-dashed border-amber-200 p-6 text-center" data-testid="dash-no-program">
            <GraduationCap className="h-10 w-10 text-amber-600 mx-auto mb-3" />
            <h3 className="font-bold text-lg">Unlock the Selective Entry Learning Centre</h3>
            <p className="text-sm text-gray-600 mt-1">Book a free assessment or enrol in a program to access topics, mocks and tutors.</p>
            <div className="flex justify-center gap-2 mt-4">
              <Link to="/get-started" className="px-4 h-10 inline-flex items-center bg-[#D3D86F] hover:bg-[#c4ca5e] text-black rounded-full font-bold uppercase tracking-wider text-xs">Book Free Assessment</Link>
              <Link to="/student/signup?program=selective-entry" className="px-4 h-10 inline-flex items-center bg-black text-[#D3D86F] rounded-full font-bold uppercase tracking-wider text-xs">Enrol Now</Link>
            </div>
          </section>
        )}

      </main>
      <Footer />
    </div>
  );
};

const Stat = ({ label, value, tone, testid, icon: Icon }) => {
  const tones = {
    amber: 'bg-amber-50 text-amber-800 border-amber-200',
    sky: 'bg-sky-50 text-sky-800 border-sky-200',
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    red: 'bg-rose-50 text-rose-800 border-rose-200',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
};

export default WinhouseDashboardPage;
